/**
 * Task #1954 — Extensions X4: helper coerenza profilo Sicurezza.
 *
 * Source of truth per il mapping `SecurityProfile → flag effettivi`
 * (`force_tls` / `force_srtp` / `force_dtls`) e → parametri SBC
 * (`transport_pref` / `srtp_mode`). Riusato BE (Zod refinement
 * `sip-extension.refinements.ts`) + FE (modale Extensions `SecurityTabContent`)
 * + migration di backfill.
 *
 * Task #2876 — il profilo è l'UNICA sorgente di verità: a override disabilitato
 * deriva sia i `force_*` sia `transport_pref`/`srtp_mode`. `force_dtls` non è
 * più un input indipendente: è derivato (true solo per `webrtc`).
 *
 * Preset flag:
 *   - `webrtc`     → TLS=true,  SRTP=true,  DTLS=true
 *   - `sip_secure` → TLS=true,  SRTP=true,  DTLS=false
 *   - `sip_legacy` → TLS=false, SRTP=false, DTLS=false
 *
 * Preset SBC:
 *   - `sip_secure` → transport `['tls']`,        srtp `mandatory`  (enforced)
 *   - `sip_legacy` → transport `['udp','tcp']`,   srtp `disabled`   (enforced)
 *   - `webrtc`     → transport NON forzato (sicurezza su path WSS/DTLS),
 *                    srtp gestito da DTLS-SRTP → preset non enforced.
 *     NB: VIETATO introdurre `wss` nell'enum `transport` (rompe CHECK DB +
 *     parità mirror Kamailio): WebRTC registra fuori da `transport_pref`.
 */

import type { SecurityProfile } from '../types/sip-extensions';
import type { KamTransport, KamSrtpMode } from '../types/kam-settings';

export interface SecurityProfileFlags {
  readonly force_tls: boolean;
  readonly force_srtp: boolean;
  readonly force_dtls: boolean;
}

const PROFILE_DEFAULTS: Readonly<Record<SecurityProfile, SecurityProfileFlags>> = {
  webrtc:     { force_tls: true,  force_srtp: true,  force_dtls: true  },
  sip_secure: { force_tls: true,  force_srtp: true,  force_dtls: false },
  sip_legacy: { force_tls: false, force_srtp: false, force_dtls: false },
};

export function getProfileDefaults(profile: SecurityProfile): SecurityProfileFlags {
  return PROFILE_DEFAULTS[profile];
}

export function profileMatchesFlags(
  profile: SecurityProfile,
  flags: SecurityProfileFlags,
): boolean {
  const preset = PROFILE_DEFAULTS[profile];
  return (
    preset.force_tls  === flags.force_tls  &&
    preset.force_srtp === flags.force_srtp &&
    preset.force_dtls === flags.force_dtls
  );
}

/**
 * `force_dtls` derivato dal profilo (Task #2876): true solo per `webrtc`.
 * Non più input utente — calcolato a partire dal profilo selezionato.
 */
export function deriveForceDtls(profile: SecurityProfile): boolean {
  return profile === 'webrtc';
}

export interface SecurityProfileSbcDefaults {
  readonly transport_pref: readonly KamTransport[];
  readonly srtp_mode: KamSrtpMode;
  /**
   * `false` = il profilo NON vincola transport/srtp (webrtc: la sicurezza è
   * enforced sul path WSS/DTLS separato). I valori restano default suggeriti
   * ma non sono soggetti al check di coerenza.
   */
  readonly enforced: boolean;
}

const PROFILE_SBC_DEFAULTS: Readonly<Record<SecurityProfile, SecurityProfileSbcDefaults>> = {
  webrtc:     { transport_pref: ['udp', 'tcp'], srtp_mode: 'disabled',  enforced: false },
  sip_secure: { transport_pref: ['tls'],        srtp_mode: 'mandatory', enforced: true  },
  sip_legacy: { transport_pref: ['udp', 'tcp'], srtp_mode: 'disabled',  enforced: true  },
};

export function getProfileSbcDefaults(profile: SecurityProfile): SecurityProfileSbcDefaults {
  return PROFILE_SBC_DEFAULTS[profile];
}

function sameTransportSet(
  a: readonly KamTransport[],
  b: readonly KamTransport[],
): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((t) => setB.has(t));
}

/**
 * Coerenza profilo ↔ parametri SBC (transport/srtp). Per i profili enforced
 * (`sip_secure`/`sip_legacy`) richiede match esatto col preset (transport
 * confrontato come insieme, order-insensitive). Per `webrtc` (non enforced)
 * ritorna sempre `true`.
 */
export function profileMatchesSbc(
  profile: SecurityProfile,
  transport_pref: readonly KamTransport[],
  srtp_mode: KamSrtpMode,
): boolean {
  const preset = PROFILE_SBC_DEFAULTS[profile];
  if (!preset.enforced) return true;
  return (
    preset.srtp_mode === srtp_mode &&
    sameTransportSet(preset.transport_pref, transport_pref)
  );
}
